/**
 * MapDrivingTimePanel — driving-time tool panel for the map toolbox.
 *
 * Project-agnostic: does not carry a project_id in analytics.
 * Renders while the driving-time tool is armed in MapToolbox. Consumes the
 * useDrivingRoute state machine for the two-point A→B picking flow.
 *
 * MapToolbox docks this panel under the filter row, on the right.
 */
import { ArrowUpDown, X } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { Button } from '@/components/atoms/button';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { useDrivingRoute } from '@/features/locations/hooks/useDrivingRoute';
import { MapCallout } from './MapCallout';
import { DrivingDistanceIcon } from './tools/DrivingDistanceIcon';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { cn } from '@/libs/utils/cn';
import { formatDistance, formatDuration } from '@/libs/utils/format';
import type { IMapboxCoordinates } from '@/types/mapbox';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCoordinates(coords: IMapboxCoordinates): string {
  return `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
}

/** The ✕ that drops an already-picked point — identical for A and B. */
function ClearPointButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      onClick={onClick}
      className="size-6 shrink-0 text-[#3c5957] hover:bg-accent"
    >
      <X className="size-3" aria-hidden="true" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MapDrivingTimePanelProps {
  /** Shell state from MapShell's render-prop (provides the live map ref). */
  shell: MapShellRenderState;
  /** Disarm — wired to ✕ and Escape. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MapDrivingTimePanel({ shell, onClose }: MapDrivingTimePanelProps) {
  const trackAction = useMixpanel((s) => s.trackAction);

  // Stable so it doesn't re-identify `calculateRoute` inside the hook and
  // re-trigger a fetch on every panel re-render (no project_id in the payload).
  const onRouteComputed = useCallback(() => {
    trackAction(ACTION_TRACKED.ITINERARY_CREATED, { success: true });
  }, [trackAction]);

  const route = useDrivingRoute({ map: shell.map, onRouteComputed });

  // Arm the route picking flow once on mount; on unmount (the tool is disarmed —
  // by ✕, Escape, or arming another tool) tear the route state down so markers,
  // the map click listener, and the route layer never leak.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount/unmount lifecycle — arm/reset are stable
  useEffect(() => {
    route.arm();
    return () => {
      route.reset();
    };
  }, []);

  // Escape → reset + close. Handled in the CAPTURE phase with
  // stopImmediatePropagation so that, when a zone is also selected, the tool
  // consumes Escape instead of MapSurface navigating away — closing the tool
  // must never clear the zone selection.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        route.reset();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [route, onClose]);

  const handleClose = () => {
    route.reset();
    onClose();
  };

  const { value: distanceValue, unit: distanceUnit } = formatDistance(route.routeInfo.distance);

  return (
    <div
      className={cn(
        // Normal rail-anchored layout — same width and card content padding
        // as DropPinToolPanel/MapWorkspaceSelectHint's helper-callout style.
        'relative w-72 p-4',
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-3 right-3 h-6 w-6 text-[#3c5957] hover:bg-accent"
        aria-label="Close driving time"
        onClick={handleClose}
      >
        <X className="size-3.5" aria-hidden="true" />
      </Button>

      <MapCallout
        title="Driving Time"
        icon={<DrivingDistanceIcon className="size-4" aria-hidden="true" />}
      />

      {/* Points + Swap */}
      <div className="flex flex-col gap-2">
        <div className="relative rounded-[10px] border border-[#e6e9e8] overflow-hidden">
          {/* Point A */}
          <div className="flex h-10 items-center gap-2 px-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-marker-a text-[11px] font-bold text-white leading-none">
              A
            </span>
            {route.pointA ? (
              <>
                <span className="flex-1 truncate text-xs text-[#0b2936]">
                  {formatCoordinates(route.pointA)}
                </span>
                <ClearPointButton label="Clear point A" onClick={route.clearPointA} />
              </>
            ) : (
              <span className="flex-1 text-xs text-[#3c5957]">Click point A on the map</span>
            )}
          </div>

          <div className="h-px bg-[#e6e9e8]" />

          {/* Point B */}
          <div className="flex h-10 items-center gap-2 px-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-marker-b text-[11px] font-bold text-white leading-none">
              B
            </span>
            {route.pointB ? (
              <>
                <span className="flex-1 truncate text-xs text-[#0b2936]">
                  {formatCoordinates(route.pointB)}
                </span>
                <ClearPointButton label="Clear point B" onClick={route.clearPointB} />
              </>
            ) : (
              <span className="flex-1 text-xs text-[#3c5957]">Click point B on the map</span>
            )}
          </div>

          {/* Swap button — centred on the divider, over the address text */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Swap points A and B"
            className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 border border-[#e6e9e8] bg-white p-0 shadow-sm"
            onClick={route.swap}
          >
            <ArrowUpDown className="size-3" aria-hidden="true" />
          </Button>
        </div>

        {/* Route result area — announced to assistive tech as it settles. */}
        <div className="mt-1 min-h-6" role="status" aria-live="polite" aria-atomic="true">
          {route.isLoading && (
            <p className="text-xs text-muted-foreground text-center">Calculating route…</p>
          )}
          {!route.isLoading && route.routeError && (
            <p className="text-xs text-destructive">Route unavailable</p>
          )}
          {!route.isLoading &&
            !route.routeError &&
            (route.pointA ?? route.pointB) &&
            route.pointA &&
            route.pointB && (
              <div className="flex items-center gap-1 text-sm">
                <span className="text-muted-foreground text-xs">Time:</span>
                <span className="font-semibold">{formatDuration(route.routeInfo.duration)}</span>
                <span className="text-muted-foreground text-xs">
                  {distanceValue}&nbsp;{distanceUnit}
                </span>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
